/**
 * useBloodTestParams Hook
 * Parses and validates blood test URL parameters from the kiosk
 */

import { useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';

/**
 * Parse blood test parameters from URL
 * @returns {Object} Blood test parameters object
 */
export function useBloodTestParams() {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const bloodTestRequired = searchParams.get('bloodTestRequired') === 'true';

    // If not required, return minimal object
    if (!bloodTestRequired) {
      return {
        bloodTestRequired: false,
        bloodTestUrl: null,
        bloodTestType: null,
        bloodTestPrice: null,
        returnUrl: null,
        timeout: 0
      };
    }

    // Get and decode the blood test URL
    let bloodTestUrl = searchParams.get('bloodTestUrl');
    if (bloodTestUrl) {
      try {
        bloodTestUrl = decodeURIComponent(bloodTestUrl);
      } catch (e) {
        // URL wasn't encoded, use as-is
      }
    }

    // Get blood test type with validation
    const rawType = searchParams.get('bloodTestType');
    const validTypes = ['hormone', 'peptide', 'general'];
    const bloodTestType = validTypes.includes(rawType) ? rawType : 'general';

    // Get price
    const bloodTestPrice = searchParams.get('bloodTestPrice') || '0';

    // Get return URL for kiosk
    const returnUrl = searchParams.get('returnUrl');

    // Get timeout (in seconds)
    const timeout = parseInt(searchParams.get('timeout') || '0', 10);

    return {
      bloodTestRequired: true,
      bloodTestUrl,
      bloodTestType,
      bloodTestPrice,
      returnUrl,
      timeout
    };
  }, [searchParams]);
}

/**
 * Get human-readable blood test type name
 * @param {string} type - Blood test type
 * @returns {string} Human-readable name
 */
export function getBloodTestTypeName(type) {
  const names = {
    hormone: 'Hormone Panel',
    peptide: 'Peptide Panel',
    general: 'General Health Panel'
  };
  return names[type] || 'Blood Test';
}

export default useBloodTestParams;
